import * as Sentry from "@sentry/browser";
import React, {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Auth0Provider,
  GetTokenSilentlyOptions,
  useAuth0,
} from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
import { useAnalytics } from "@/lib/analytics/analytics";
import { portalAuthView, PortalTokenState } from "./portal_auth_view";
import { isPortalCallbackPath } from "./portal_paths";

interface AuthPortalProviderProps {
  children: React.ReactNode;
  // Rendered in place of `children` for a visitor with no session, at whatever
  // address they asked for. Passed in rather than imported so this provider stays
  // unaware of the pages it gates.
  signedOut: React.ReactNode;
  domain: string;
  clientId: string;
  audience: string;
  redirectUri: string;
}

export function AuthPortalProvider(props: AuthPortalProviderProps) {
  const { children, signedOut, domain, clientId, audience, redirectUri } =
    props;

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      audience={audience}
      redirectUri={redirectUri}
    >
      <AuthProvider signedOut={signedOut}>{children}</AuthProvider>
    </Auth0Provider>
  );
}

interface AuthContext {
  token: string;
  user: any;
  setToken: React.Dispatch<React.SetStateAction<string>>;
  getTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<any>;
}

const AuthContext = createContext<AuthContext>({
  token: "",
  user: null,
  setToken: () => {},
  getTokenSilently: () => Promise.resolve(),
});

// A settling session was observed rejecting fetches for over a second before
// accepting them, and a rejection comes back in a fraction of that — so retries
// must be spaced, not just counted, to outlast it. Two spaced retries cover the
// observed window without holding anyone long on a genuinely unobtainable token.
export const TOKEN_FETCH_RETRIES = 2;
const TOKEN_FETCH_RETRY_DELAY_MS = 400;

interface AuthProviderProps {
  children: React.ReactNode;
  signedOut: React.ReactNode;
}

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider(props: AuthProviderProps) {
  const { children, signedOut } = props;
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } =
    useAuth0();
  const [token, setToken] = useState("");
  const [tokenState, setTokenState] = useState<PortalTokenState>("pending");
  const { pathname } = useLocation();
  const analytics = useAnalytics();

  useEffect(() => {
    if (!user) {
      return;
    }

    Sentry.setUser({ id: user.sub, email: user.email });
    analytics.identify(user.sub || "", {
      name: user.name,
      email: user.email,
      phone: user.phone_number,
      source: "Portal",
    });
  }, [analytics, user]);
  useEffect(() => {
    if (isLoading) {
      return;
    }

    let abandoned = false;

    // The first fetch on a fresh page can reject while the session is still
    // settling, then succeed moments later. Reporting the first rejection as "no
    // session" would paint the login form over someone who is signed in, so a
    // rejection only counts once retrying has stopped helping.
    const fetchToken = (attemptsLeft: number) => {
      getAccessTokenSilently()
        .then((t) => {
          if (abandoned) {
            return;
          }
          setToken(t);
          setTokenState("present");
        })
        .catch((error) => {
          if (abandoned) {
            return;
          }
          if (attemptsLeft > 0) {
            const retry = () => {
              if (!abandoned) {
                fetchToken(attemptsLeft - 1);
              }
            };
            setTimeout(retry, TOKEN_FETCH_RETRY_DELAY_MS);
            return;
          }
          console.error("Unauthenticated", error);
          setTokenState("failed");
        });
    };

    fetchToken(TOKEN_FETCH_RETRIES);

    return () => {
      abandoned = true;
    };
  }, [isLoading, getAccessTokenSilently]);

  const view = portalAuthView({
    isLoading,
    isAuthenticated,
    hasUser: Boolean(user),
    token: tokenState,
    completingSignIn: isPortalCallbackPath(pathname),
  });

  if (view === "loading") {
    return <Fragment />;
  }

  if (view === "signedOut") {
    return <Fragment>{signedOut}</Fragment>;
  }

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        setToken,
        getTokenSilently: getAccessTokenSilently,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
